import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Post, Get } from '../../api/server';
import { toast } from 'react-toastify';
import NavBar from '../../layouts/NavBar';
import Footer from '../../components/footers/Footer';
import GlobalContext from '../../contexts/store';
import DarkGradient from '../../components/DarkGradient';
import SiteButton from '../../components/rj-components/SiteButton';

// Phone number cleaning and formatting utility
const cleanPhoneNumber = (phone) => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Return cleaned number (will be 10-11 digits)
  return cleaned;
};

const formatPhoneForDisplay = (phone) => {
  const cleaned = cleanPhoneNumber(phone);

  // Format based on length
  if (cleaned.length === 10) {
    // Format as (XXX) XXX-XXXX
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // Format as +1 (XXX) XXX-XXXX
    return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
  }

  // Return as-is if not 10 or 11 digits
  return phone;
};

const validatePhoneNumber = (phone) => {
  const cleaned = cleanPhoneNumber(phone);

  // Check if it's 10 digits or 11 digits starting with 1
  if (cleaned.length === 10) {
    return { isValid: true, cleaned };
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return { isValid: true, cleaned };
  } else {
    return { isValid: false, cleaned };
  }
};

const FormInput = ({ label, name, type, placeholder, value, onChange, required, className = "", style }) => {
  // Check phone validation for visual feedback
  const isPhoneField = name === 'phone';
  const phoneValidation = isPhoneField && value ? validatePhoneNumber(value) : { isValid: true };
  const showPhoneError = isPhoneField && value && !phoneValidation.isValid;

  return (
    <div className={`space-y-2 ${className}`} style={style}>
      <label htmlFor={name} className="block text-sm font-medium text-white">
        {label} {required && <span className="text-red-400">*</span>}
        {isPhoneField && value && (
          <span className={`ml-2 text-xs ${phoneValidation.isValid ? 'text-green-400' : 'text-red-400'}`}>
            {phoneValidation.isValid ? '✓' : '⚠ Invalid format'}
          </span>
        )}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className={`w-full bg-[#1A1D20] border px-4 py-3 rounded-lg 
                   focus:outline-none transition-colors duration-300 
                   placeholder-gray-400 [color-scheme:dark] placeholder:text-gray-400 placeholder:text-sm placeholder:font-light placeholder:font-semibold text-white
                   ${showPhoneError
            ? 'border-red-500 focus:border-red-400'
            : isPhoneField && value && phoneValidation.isValid
              ? 'border-green-500 focus:border-green-400'
              : 'border-gray-600 focus:border-white'
          }`}
      />
      {showPhoneError && (
        <p className="text-red-400 text-xs mt-1">
          Please enter a valid 10 or 11 digit phone number
        </p>
      )}
    </div>
  );
};

function VipSignup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useContext(GlobalContext);
  const [pageData, setPageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    dob: '',
    city: '',
    school: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    async function getData() {
      try {
        setLoading(true);
        const response = await Get(`/submissions/qrcode/linkcheck/${id}`);
        if (response?.active) {
          setPageData(response.submission);
        } else {
          toast('This VIP signup link has expired or is no longer valid.', {
            type: 'warning',
            theme: 'dark',
            position: "top-right",
            autoClose: 5000,
          });
          navigate('/');
        }
      } catch (err) {
        console.log('VIP signup error', err);
        toast('Unable to load VIP signup page. Please try again later.', {
          type: 'error',
          theme: 'dark',
          position: "top-right",
          autoClose: 5000,
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      getData();
    }
  }, [id, navigate]);

  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      // Clean and format phone number
      const formattedPhone = formatPhoneForDisplay(value);
      setValues({ ...values, [name]: formattedPhone });
    } else {
      setValues({ ...values, [name]: value });
    }
  };

  const resetForm = () => {
    setValues({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      dob: '',
      city: '',
      school: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!values.first_name || !values.last_name || !values.phone || !values.email || !values.dob) {
      toast('Please fill out all required fields.', {
        type: 'error',
        theme: 'dark',
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumber(values.phone);
    if (!phoneValidation.isValid) {
      toast('Please enter a valid 10 or 11 digit phone number.', {
        type: 'error',
        theme: 'dark',
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    try {
      setSubmitting(true);
      await Post('/users/qr/create', {
        first_name: values.first_name,
        last_name: values.last_name,
        phone: phoneValidation.cleaned, // Send cleaned phone number
        email: values.email,
        dob: new Date(values.dob),
        city: values.city,
        user_type: pageData?.usertype?._id,
        company: pageData?.company,
        submission: pageData?._id,
        school: values.school
      });

      toast('Welcome to the VIP experience! Check your email for exclusive updates and perks.', {
        type: 'success',
        theme: 'dark',
        position: "top-right",
        autoClose: 7000,
      });

      resetForm();
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err) {
      console.log('VIP signup submission error', err);
      toast('Something went wrong. Please try again or contact us for assistance.', {
        type: 'error',
        theme: 'dark',
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#101010] flex items-center justify-center">
        <div className="text-white text-xl">Loading VIP signup...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#101010]">

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video/Image */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute top-0 left-0 w-full h-full object-cover"
          >
            <source src="https://sfclubs.s3.us-west-1.amazonaws.com/videos/newIntro.mov" type="video/mp4" />
          </video>
          <DarkGradient />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pb-[100px]">

            {/* Left Side - Marketing Content */}
            <div className="text-white space-y-6">
              <div>
                <h1 className="text-6xl md:text-7xl font-bold mb-4" style={{ fontFamily: "goldbold" }}>
                  <span className="text-yellow-400">VIP</span><br />
                  <span className="text-white">ACCESS</span>
                </h1>
                <div className="w-24 h-1 bg-yellow-400 mb-6"></div>
              </div>

              <h2 className="text-2xl md:text-3xl font-light mb-6">
                Join the <span className="text-yellow-400 font-semibold uppercase">
                  {pageData?.usertype?.name || 'Exclusive'}
                </span> Experience
              </h2>

              <div className="space-y-4 text-lg">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-3"></div>
                  <p>Priority entry and {pageData?.usertype?.name || 'Exclusive'} bottle service</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-3"></div>
                  <p>Exclusive event invitations and presales</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-3"></div>
                  <p>Complimentary tickets to select events</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-3"></div>
                  <p>Personal event concierge service</p>
                </div>
              </div>

              <p className="text-gray-300 text-sm mt-8">
                We value your privacy - your information is never sold or shared.
              </p>
            </div>

            {/* Right Side - Form */}
            <div className="bg-black/60 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Become a Exclusive Member
                </h3>
                <p className="text-gray-300">
                  Complete your details to unlock exclusive nightlife privileges
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="First Name"
                    name="first_name"
                    type="text"
                    placeholder="Enter your first name"
                    value={values.first_name}
                    onChange={onChange}
                    required
                  />
                  <FormInput
                    label="Last Name"
                    name="last_name"
                    type="text"
                    placeholder="Enter your last name"
                    value={values.last_name}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Phone"
                    name="phone"
                    type="tel"
                    placeholder="Enter phone number (10-11 digits)"
                    value={values.phone}
                    onChange={onChange}
                    required
                  />
                  <FormInput
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={values.email}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    placeholder=""
                    value={values.dob}
                    onChange={onChange}
                    required
                  />
                  <FormInput
                    label="City"
                    name="city"
                    type="text"
                    placeholder="San Francisco"
                    value={values.city}
                    onChange={onChange}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <FormInput
                    style={{ gridColumn: 'span 2' }}
                    label="School/University"
                    name="school"
                    type="text"
                    placeholder="Enter your School / University Name"
                    value={values.school}
                    onChange={onChange}
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-4 px-8 
                             rounded-lg transition-all duration-300 transform hover:scale-[1.02] 
                             disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {submitting ? 'Joining VIP...' : 'Join VIP Experience'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  By joining, you agree to receive VIP updates and exclusive offers.
                  You can unsubscribe at any time.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VipSignup; 